import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import DeleteIcon from "@mui/icons-material/Delete";
import React from "react";
import type {
    IODataEntityDescriptor,
    IODataFilterRow,
    IODataMetadataCatalog,
    IODataPropertyDescriptor,
    ODataOperator,
} from "../declarations/ODataExplorer";
import { allowedOperators, valueControlKind } from "./utilities/edmTypes";

interface IFilterRowProps {
    catalog: IODataMetadataCatalog;
    entity: IODataEntityDescriptor;
    onChange: (id: string, next: IODataFilterRow) => void;
    onRemove: (id: string) => void;
    row: IODataFilterRow;
}

function FilterRow({
    catalog,
    entity,
    onChange,
    onRemove,
    row,
}: IFilterRowProps) {
    const prop: IODataPropertyDescriptor | undefined = entity.properties.find(
        (p) => p.name === row.property,
    );
    const ops: ODataOperator[] = prop
        ? allowedOperators(prop.edmType, prop.enumTypeName)
        : [];
    const kind = prop
        ? valueControlKind(prop.edmType, prop.enumTypeName, row.operator)
        : "none";
    const enumValues = prop?.enumTypeName
        ? (catalog.enums[prop.enumTypeName]?.values ?? [])
        : [];

    return (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Autocomplete
                getOptionLabel={(o) => o}
                onChange={(_, v) =>
                    onChange(row.id, {
                        ...row,
                        property: v ?? "",
                        operator: "eq",
                        value: "",
                    })
                }
                options={entity.properties
                    .filter((p) => p.isFilterable !== false)
                    .map((p) => p.name)}
                renderInput={(params) => (
                    <TextField {...params} label="Property" />
                )}
                sx={{ minWidth: 220 }}
                value={row.property || null}
                size="small"
            />
            <TextField
                disabled={!prop}
                label="Operator"
                size="small"
                onChange={(e) =>
                    onChange(row.id, {
                        ...row,
                        operator: e.target.value as ODataOperator,
                        value: row.value,
                    })
                }
                select
                sx={{ minWidth: 160 }}
                value={row.operator}
            >
                {ops.map((op) => (
                    <MenuItem key={op} value={op}>
                        {op}
                    </MenuItem>
                ))}
            </TextField>
            {kind === "none" && <span style={{ width: 240 }} />}
            {kind === "text" && (
                <TextField
                    label="Value"
                    onChange={(e) =>
                        onChange(row.id, { ...row, value: e.target.value })
                    }
                    sx={{ minWidth: 240 }}
                    value={row.value}
                    size="small"
                />
            )}
            {kind === "number" && (
                <TextField
                    label="Value"
                    onChange={(e) =>
                        onChange(row.id, { ...row, value: e.target.value })
                    }
                    sx={{ minWidth: 160 }}
                    type="number"
                    value={row.value}
                    size="small"
                />
            )}
            {kind === "date" && (
                <TextField
                    label="Value"
                    onChange={(e) =>
                        onChange(row.id, { ...row, value: e.target.value })
                    }
                    sx={{ minWidth: 220 }}
                    type="datetime-local"
                    value={row.value}
                    size="small"
                />
            )}
            {kind === "boolean" && (
                <TextField
                    label="Value"
                    onChange={(e) =>
                        onChange(row.id, { ...row, value: e.target.value })
                    }
                    select
                    size="small"
                    sx={{ minWidth: 120 }}
                    value={row.value || "true"}
                >
                    <MenuItem value="true">true</MenuItem>
                    <MenuItem value="false">false</MenuItem>
                </TextField>
            )}
            {kind === "enum" && (
                <TextField
                    label="Value"
                    onChange={(e) =>
                        onChange(row.id, { ...row, value: e.target.value })
                    }
                    select
                    sx={{ minWidth: 200 }}
                    value={row.value}
                    size="small"
                >
                    {enumValues.map((v) => (
                        <MenuItem key={v.name} value={v.name}>
                            {v.name}
                        </MenuItem>
                    ))}
                </TextField>
            )}
            <IconButton
                aria-label="Remove filter"
                onClick={() => onRemove(row.id)}
                size="small"
            >
                <DeleteIcon fontSize="small" />
            </IconButton>
        </Stack>
    );
}

export default React.memo(FilterRow);
